import type WaButton from "@web.awesome.me/webawesome-pro/dist/components/button/button.js";
import htmx from "htmx.org";
import { css, html } from "lit";
import { z } from "zod";
import { checkNotNil } from "../../check-not-nil";
import { type FormSubmissionHandler, formSubmissionHandler } from "../../form";
import { JsonBootstrapper } from "../../handlers/json-bootstrapper";
import {
  type ParserSchema,
  type ParserType,
  parser,
} from "../../schemas/parser";
import { Tags } from "../../schemas/tag";
import { ValidationError } from "../../schemas/validation-error";
import { hostStyles } from "../../styles/host.styles";
import { WmElement } from "../element";
import type { WmNoteContent } from "./content";
import type { WmNoteTitle } from "./title.js";

type Payload = ParserType<typeof Payload>;
const Payload = parser(
  z.strictObject({
    id: z.string(),
    title: z.string(),
    content: z.optional(z.string()).default(""),
    selectedTags: Tags.schema,
  }),
);

export class WmNoteEdit extends WmElement {
  static readonly TAG_NAME = "wm-note-edit";

  static styles = [
    hostStyles,
    css`
      form {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        gap: var(--wa-space-m);
      }

      wa-button {
        align-self: start;
      }
    `,
  ];

  #autoPopulateTitle: boolean;
  #formSubmit?: FormSubmissionHandler;
  #jsonBootstrapper: JsonBootstrapper<ParserSchema<typeof Payload>>;
  #payload: Payload;

  constructor() {
    super();
    this.#autoPopulateTitle = false;
    this.#jsonBootstrapper = new JsonBootstrapper({
      schema: Payload.schema,
      callback: (v) => {
        const contentElement = document.createElement("wm-note-content");
        contentElement.value = v.content;
        if (v.title === contentElement.computedTitle) {
          this.#autoPopulateTitle = true;
        }
        this.#payload = v;
        this.#formSubmit = formSubmissionHandler(`/app/notes/${v.id}`, {
          method: "PUT",
        })
          .on("pre:response", () => (this.#querySubmitButton().loading = true))
          .on("response", (r) => this.#handleResponse(r))
          .on(
            "post:response",
            () => (this.#querySubmitButton().loading = false),
          )
          .on("error", (err) =>
            this.logger.error("unable to PUT /app/notes", err),
          );
        this.requestUpdate();
      },
      logger: this.logger,
    });
    this.#payload = {
      id: "",
      title: "",
      content: "",
      selectedTags: [],
    };
  }

  protected override render() {
    const availableTags = this.#payload.selectedTags ?? [];
    const selectedTagIds = availableTags.map((t) => t.id);

    return html`
      <slot @slotchange=${(e: Event) => this.#jsonBootstrapper.slotChanged(e)}></slot>
      <form @submit=${{ handleEvent: this.#formSubmit }}>
        <wm-output label="Id">${this.#payload.id}</wm-output>
        <wm-note-title
          .value=${this.#payload.title}
          @blur=${this.#handleTitleBlur}
          @input=${this.#handleTitleInput}>
        </wm-note-title> 
        <wm-note-content
          .value=${this.#payload.content}
          @input=${this.#handleContentInput}>
        </wm-note-content>
        <wm-tags-combobox
          name="tags"
          .availableTags=${availableTags}
          .selectedTagIds=${selectedTagIds}>
        </wm-tags-combobox>
        <wa-button variant="success" type="submit">Modify</wa-button>
      </form>
    `;
  }

  async #handleResponse(response: Response) {
    // Handle 422 with JSON serialized error messages.
    if (response.status === 422) {
      const validationError = ValidationError.parse(await response.json());
      const { path, reason } = validationError.error;

      if (path === "title") {
        const titleInput = this.#queryTitleInput();
        titleInput.error = reason;
      } else if (path === "content") {
        const contentInput = this.#queryContentInput();
        contentInput.error = reason;
      } else {
        this.logger.warn(
          "unknown invalid field from 422 response: %s",
          path,
          validationError,
        );
      }
    } else {
      return htmx.ajax("GET", "/app/notes", { push: "true" });
    }
  }

  async #handleContentInput(_: InputEvent) {
    const titleInput = this.#queryTitleInput();
    const contentInput = this.#queryContentInput();
    await Promise.all([titleInput.updateComplete, contentInput.updateComplete]);
    if (this.#autoPopulateTitle) {
      titleInput.value = contentInput.computedTitle;
    } else if (!titleInput.value && contentInput.value) {
      this.#autoPopulateTitle = true;
      titleInput.value = contentInput.computedTitle;
    }
  }

  // TODO: find a better behavior than blur. This also occurs if the user
  // clicks modify:
  // - The blur event is fired
  // - The field is auto populated
  // - The request is submitted
  // Resulting in a little bit of flash screen. Ideally, it would show a
  // validation message that the field is required instead.
  async #handleTitleBlur(_: FocusEvent) {
    const titleInput = this.#queryTitleInput();
    const contentInput = this.#queryContentInput();
    await Promise.all([titleInput.updateComplete, contentInput.updateComplete]);
    if (!titleInput.value) {
      this.#autoPopulateTitle = true;
      titleInput.value = contentInput.computedTitle;
    }
  }

  #handleTitleInput(_: InputEvent) {
    this.#autoPopulateTitle = false;
  }

  #queryTitleInput(): WmNoteTitle {
    return this.queryNotNil("wm-note-title");
  }

  #queryContentInput(): WmNoteContent {
    return checkNotNil(this.renderRoot.querySelector("wm-note-content"));
  }

  #querySubmitButton(): WaButton {
    return this.queryNotNil(`wa-button[type="submit"]`);
  }
}

customElements.define(WmNoteEdit.TAG_NAME, WmNoteEdit);

declare global {
  interface HTMLElementTagNameMap {
    [WmNoteEdit.TAG_NAME]: WmNoteEdit;
  }
}
